/**
 * Pantalla de reporte: parámetros arriba, cuadro abajo, PDF al costado.
 *
 * Por qué existe
 * --------------
 * Un balance de sumas y saldos no es "una operación sobre un registro": es una
 * pregunta sobre una empresa y un período. Mientras los estados contables sólo
 * colgaban de la ficha de una cuenta, de un período o de un ejercicio, para ver
 * el mayor había que saber de antemano en qué formulario estaba escondido el
 * botón. Esta pantalla los pone en el menú, que es donde se los busca.
 *
 * Es GENÉRICA: no sabe de contabilidad ni de IVA. La declara el módulo con una
 * acción de ventana de `view_modes=("report",)` y un `context.report_action` que
 * nombra la acción de servidor que hace la cuenta. Todo lo demás sale de la
 * metadata:
 *
 * - los **parámetros** son los `ActionParam` que la acción declara, dibujados
 *   con los mismos controles que el diálogo de acciones (fecha con el
 *   `DatePickerInput` del ERP, many2one con el buscador por nombre);
 * - el **cuadro** es el `table` que devuelve la acción, dibujado por
 *   `TabularReport`;
 * - el **PDF** sale del `print` que trae el propio cuadro, así que baja
 *   exactamente el período que se está mirando y no uno que el cliente arme por
 *   su cuenta.
 *
 * Sobre qué registro corre
 * ------------------------
 * Sobre la **empresa activa**, la del selector de arriba a la derecha. Es la
 * única respuesta correcta: un balance de dos empresas mezcladas no es un
 * balance. Si no hay empresa activa, la pantalla lo dice y no ofrece un botón
 * que va a fallar.
 */

import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ErrorState } from '@/components/shell/primitives';
import { Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { api } from '@/lib/api';
import type { JsonValue, ModelSpec, TabularPrint, TabularReportData } from '@/lib/types';
import {
  ActionParamControl,
  cleanParamValues,
  initialParamValues,
  type ActionParamValues,
} from '@/views/ActionDialog';
import { guardarArchivo } from '@/views/ReportBar';
import { leerReporteTabular, TabularReport } from '@/views/TabularReport';
import { useSessionStore } from '@/store/session';

/**
 * Botón «Descargar PDF» de un cuadro ya calculado.
 *
 * Se alimenta del `print` que trae el propio cuadro —modelo, registro, nombre
 * del reporte y parámetros—, así que el PDF es del mismo período que la
 * pantalla. Es genérico: sirve para cualquier reporte tabular de cualquier
 * módulo, presente o futuro.
 */
export function ReportPdfButton({ print, size = 'md' }: { print: TabularPrint; size?: 'sm' | 'md' }) {
  const bajar = useMutation({
    mutationFn: () => api.downloadReport(print.model, print.id, print.report, print.params),
    onSuccess: ({ blob, filename }) => {
      guardarArchivo(blob, filename);
    },
    onError: (error) => {
      notify.error('No se pudo generar el PDF', error instanceof Error ? error.message : undefined);
    },
  });

  return (
    <Button
      size={size}
      variant="secondary"
      loading={bajar.isPending}
      onClick={() => {
        bajar.mutate();
      }}
    >
      Descargar PDF
    </Button>
  );
}

/**
 * Botón «Exportar a Excel» de un cuadro ya calculado.
 *
 * Es el que faltaba. El PDF sirve para archivar y para mandarle el balance al
 * consejo de administración; el contador necesita **sumar la columna**, y para
 * eso el archivo tiene que ser una planilla con importes numéricos y fechas de
 * verdad. El backend vuelve a correr la misma acción con los mismos parámetros,
 * así que la planilla es del período que se está mirando.
 *
 * Genérico como el de PDF: no sabe qué reporte es. Cualquier cuadro de cualquier
 * módulo lo hereda sin tocar este archivo.
 */
export function ReportExcelButton({
  model,
  id,
  action,
  params,
  size = 'md',
}: {
  model: string;
  id: number;
  action: string;
  params: Record<string, JsonValue>;
  size?: 'sm' | 'md';
}) {
  const bajar = useMutation({
    mutationFn: () => api.exportTabularReport(model, id, action, params, 'xlsx'),
    onSuccess: ({ blob, filename }) => {
      guardarArchivo(blob, filename);
    },
    onError: (error) => {
      notify.error(
        'No se pudo generar la planilla',
        error instanceof Error ? error.message : undefined,
      );
    },
  });

  return (
    <Button
      size={size}
      variant="secondary"
      loading={bajar.isPending}
      onClick={() => {
        bajar.mutate();
      }}
    >
      Exportar a Excel
    </Button>
  );
}

export interface ReportScreenProps {
  /** Metadata del modelo sobre el que corre la acción (normalmente `base.company`). */
  spec: ModelSpec;
  /** Nombre técnico de la acción de servidor que calcula el cuadro. */
  actionName: string;
  /** Título de la pantalla: el del menú por el que se entró. */
  title: string;
}

export function ReportScreen({ spec, actionName, title }: ReportScreenProps) {
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const accion = (spec.actions ?? []).find((a) => a.name === actionName) ?? null;

  const [valores, setValores] = useState<ActionParamValues>({});
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [cuadro, setCuadro] = useState<TabularReportData | null>(null);
  // Los parámetros CON LOS QUE SE CALCULÓ el cuadro que está en pantalla, que no
  // son necesariamente los que hay ahora en los controles: si alguien cambia el
  // «hasta» y no vuelve a apretar «Ver reporte», la planilla tiene que salir del
  // período que se está mirando y no de uno que nadie calculó.
  const [paramsDelCuadro, setParamsDelCuadro] = useState<Record<string, JsonValue>>({});

  // Al cambiar de reporte —o de empresa— se vuelve a los valores por defecto y
  // se descarta el cuadro anterior: dejarlo en pantalla con los parámetros de
  // otro reporte mostraría números que ya no son de lo que dice el título.
  useEffect(() => {
    setValores(accion === null ? {} : initialParamValues(accion));
    setErrores({});
    setCuadro(null);
    setParamsDelCuadro({});
  }, [accion, companyId]);

  const correr = useMutation({
    mutationFn: (params: ActionParamValues) => {
      if (companyId === null) throw new Error('No hay empresa activa.');
      return api.callAction(spec.model, companyId, actionName, params);
    },
    onSuccess: (resultado, params) => {
      const leido = leerReporteTabular(resultado);
      if (leido === null) {
        // El servidor contestó algo que no es un cuadro. No se inventa un
        // dibujo: se muestra lo que dijo.
        notify.info(title, resultado.message);
        return;
      }
      setCuadro(leido);
      setParamsDelCuadro(params as Record<string, JsonValue>);
    },
    onError: (error) => {
      notify.error('No se pudo generar el reporte', error instanceof Error ? error.message : undefined);
    },
  });

  if (accion === null) {
    return (
      <ErrorState
        title="Este reporte no está disponible"
        message={
          `El reporte «${title}» necesita la operación «${actionName}» sobre ${spec.label}, y tu ` +
          'usuario no la tiene habilitada o el módulo que la declara no está instalado.'
        }
      />
    );
  }

  if (companyId === null) {
    return (
      <ErrorState
        title="Elegí una empresa"
        message="Un estado contable es siempre de una empresa. Elegí una en el selector de arriba y volvé a entrar."
      />
    );
  }

  const params = accion.params ?? [];

  const generar = (): void => {
    const problemas: Record<string, string> = {};
    for (const param of params) {
      if (!param.required) continue;
      const valor = valores[param.name];
      const vacio = valor === null || valor === undefined || (typeof valor === 'string' && valor.trim() === '');
      if (vacio) problemas[param.name] = 'Este dato es obligatorio.';
    }
    setErrores(problemas);
    if (Object.keys(problemas).length > 0) return;
    correr.mutate(cleanParamValues(accion, valores));
  };

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6">
      <header>
        <h1 className="text-xl font-semibold">{title}</h1>
        {accion.help !== undefined && accion.help !== '' && (
          <p className="mt-1 text-sm text-fg-muted">{accion.help}</p>
        )}
      </header>

      {/* Los parámetros del reporte. Es un `div` y no un `form` para no depender
          del submit nativo, igual que en el diálogo de acciones; Enter se
          maneja a mano. */}
      <section
        aria-label="Parámetros del reporte"
        className="rounded-xl border border-line bg-surface p-4"
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' && (evento.target as HTMLElement).tagName !== 'TEXTAREA') {
            evento.preventDefault();
            generar();
          }
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {params.map((param) => (
            <ActionParamControl
              key={param.name}
              param={param}
              valor={valores[param.name] ?? null}
              error={errores[param.name] ?? null}
              onChange={(valor: JsonValue) => {
                setValores((actual) => ({ ...actual, [param.name]: valor }));
                setErrores((actual) => {
                  if (actual[param.name] === undefined) return actual;
                  const copia = { ...actual };
                  delete copia[param.name];
                  return copia;
                });
              }}
            />
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="primary" loading={correr.isPending} onClick={generar}>
            Ver reporte
          </Button>
        </div>
      </section>

      {cuadro === null ? (
        <p className="rounded-xl border border-dashed border-line px-3 py-10 text-center text-sm text-fg-muted">
          Completá el período y apretá «Ver reporte».
        </p>
      ) : (
        <section className="rounded-xl border border-line bg-surface p-4">
          <TabularReport
            datos={cuadro}
            acciones={
              <div className="flex flex-wrap gap-2">
                <ReportExcelButton
                  model={spec.model}
                  id={companyId}
                  action={actionName}
                  params={paramsDelCuadro}
                />
                {cuadro.print !== null && <ReportPdfButton print={cuadro.print} />}
              </div>
            }
          />
        </section>
      )}
    </div>
  );
}
